import { useNavigate } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { showToast } from "../../../toast";
import { formatWatchTogetherCountdown } from "../../../utils";
import { unreadGiftCount } from "../../../giftsApi";
import { startScheduledTogetherApi } from "../../../togetherApi";
import { resolveMediaIndex } from "../together/togetherSync";

// Named tiers (not raw numbers scattered per-status) so the ordering
// intent stays legible and matches the approved priority scheme exactly:
// an incoming invite always wins, a ready-to-act state (ready to watch, an
// unread gift) beats an already-in-progress/committed one (active,
// pending, scheduled), which in turn beats a plain idle card. Cards only
// move tiers on a real event (an invite arriving, a session starting) -
// never on a per-render/no-op change - so Home doesn't reshuffle for no
// reason.
const TIER_INVITE = 100;
const TIER_READY = 90;
const TIER_ACTIVE = 70;
const TIER_IDLE = 10;

export interface ActivityCardConfig {
  id: string;
  title: string;
  description: string;
  icon: string;
  status: "idle" | "scheduled" | "ready" | "pending" | "active" | "invite";
  statusText: string;
  badge?: string;
  actionLabel: string;
  interactive: boolean;
  priority: number;
  onActivate?: () => void;
}

// Data-driven activity card config, one builder per feature, each reading
// only appState that already exists elsewhere in the app (Watch Together,
// Play Together, Listen Together, Gifts). Sorted by `priority` (highest
// first) by the caller (ActivityCarousel.tsx) so an incoming invite or a
// ready-to-start session always surfaces first regardless of display
// order. See the plan doc for the full per-card status-priority rationale.
export function useActivityCards(): ActivityCardConfig[] {
  const navigate = useNavigate();

  const togetherIncomingInvite = useAppState((s) => s.togetherIncomingInvite);
  const togetherSession = useAppState((s) => s.togetherSession);
  const togetherSchedules = useAppState((s) => s.togetherSchedules);
  const media = useAppState((s) => s.media);

  const gameIncomingInvite = useAppState((s) => s.gameIncomingInvite);
  const gameSession = useAppState((s) => s.gameSession);

  const partyIncomingInvite = useAppState((s) => s.partyIncomingInvite);
  const partySession = useAppState((s) => s.partySession);

  const gifts = useAppState((s) => s.gifts);

  const wheelIncomingInvite = useAppState((s) => s.wheelIncomingInvite);
  const wheelSession = useAppState((s) => s.wheelSession);

  // --- Watch Together ---
  const readySchedule = togetherSchedules.find((s) => s.status === "ready") ?? null;
  const nextScheduled = togetherSchedules.find((s) => s.status === "scheduled") ?? null;

  let watch: ActivityCardConfig;
  if (togetherIncomingInvite) {
    watch = {
      id: "watch",
      title: "Watch Together",
      description: "Watch something together in sync",
      icon: "bi-film",
      status: "invite",
      statusText: `${togetherIncomingInvite.fromName} wants to watch together`,
      interactive: false,
      actionLabel: "Waiting",
      priority: TIER_INVITE,
    };
  } else if (togetherSession?.status === "active") {
    watch = {
      id: "watch",
      title: "Watch Together",
      description: "Watch something together in sync",
      icon: "bi-film",
      status: "active",
      statusText: `Watching with ${togetherSession.peerName}`,
      badge: "Live",
      actionLabel: "Resume",
      interactive: true,
      priority: TIER_ACTIVE,
      onActivate: () => {
        navigate("/gallery");
        const idx = resolveMediaIndex(media, togetherSession.filename);
        if (idx === -1) {
          showToast("Can't find that item in your gallery.", "error");
          return;
        }
        appState.currentMediaIndex = idx;
        notifyStateChange();
      },
    };
  } else if (togetherSession?.status === "pending") {
    watch = {
      id: "watch",
      title: "Watch Together",
      description: "Watch something together in sync",
      icon: "bi-film",
      status: "pending",
      statusText: `Waiting for ${togetherSession.peerName}…`,
      interactive: false,
      actionLabel: "Waiting",
      priority: TIER_ACTIVE,
    };
  } else if (readySchedule) {
    watch = {
      id: "watch",
      title: "Watch Together",
      description: "Watch something together in sync",
      icon: "bi-film",
      status: "ready",
      statusText: "It's time to watch.",
      badge: "Ready",
      actionLabel: "Start",
      interactive: true,
      priority: TIER_READY,
      // Fixed a pre-existing bug: this used to set togetherInviteDialogOpen,
      // which opens the CREATE-a-new-invite form, not anything that starts
      // the already-ready session - readySchedule.id needs
      // startScheduledTogetherApi (the same call TogetherRoot.tsx's own
      // togetherScheduleReminder toast banner already uses), which actually
      // flips it to an active session. TogetherRoot's existing
      // active-session effect takes it from there (auto-opens the Lightbox
      // once appState.togetherSession.status is "active").
      onActivate: () => {
        startScheduledTogetherApi(readySchedule.id).then((result) => {
          if (!result.ok) showToast(result.error || "Failed to start.", "error");
        });
      },
    };
  } else if (nextScheduled) {
    watch = {
      id: "watch",
      title: "Watch Together",
      description: "Watch something together in sync",
      icon: "bi-film",
      status: "scheduled",
      statusText: formatWatchTogetherCountdown(nextScheduled.startsAt),
      actionLabel: "View",
      interactive: true,
      priority: TIER_ACTIVE,
      // The dedicated page's Upcoming section (WatchTogetherForm.tsx,
      // reused there too) is a better home for this than the modal.
      onActivate: () => navigate("/watch-together"),
    };
  } else {
    // Starting a NEW session needs a specific video already selected -
    // WatchTogetherForm has no meaningful "now" mode without a filename.
    // So the idle card sends the user to the dedicated picker page instead
    // of trying to start anything directly.
    watch = {
      id: "watch",
      title: "Watch Together",
      description: "Pick a video and watch it together.",
      icon: "bi-film",
      status: "idle",
      statusText: "Pick a video and watch it together.",
      actionLabel: "Choose Video",
      interactive: true,
      priority: TIER_IDLE,
      onActivate: () => navigate("/watch-together"),
    };
  }

  // --- Play Together (Game) ---
  // Game/Listen Together active states are informational-only in this pass
  // (GameStage already covers the whole screen the instant a session
  // exists - there's nothing for a Home card to "resume" into, unlike
  // Watch Together above). See the plan doc: giving all three features a
  // uniform "resumable session" contract is tracked as follow-up work, not
  // solved ad hoc here.
  let play: ActivityCardConfig;
  if (gameIncomingInvite) {
    play = {
      id: "play",
      title: "Play Together",
      description: "Questions, Truth or Dare & Spicy decks",
      icon: "bi-controller",
      status: "invite",
      statusText: `${gameIncomingInvite.fromName} wants to play`,
      interactive: false,
      actionLabel: "Waiting",
      priority: TIER_INVITE,
    };
  } else if (gameSession) {
    play = {
      id: "play",
      title: "Play Together",
      description: "Questions, Truth or Dare & Spicy decks",
      icon: "bi-controller",
      status: gameSession.status === "pending" ? "pending" : "active",
      statusText: gameSession.status === "pending" ? `Waiting for ${gameSession.peerName}…` : `Playing with ${gameSession.peerName}`,
      interactive: false,
      actionLabel: "In progress",
      priority: TIER_ACTIVE,
    };
  } else {
    play = {
      id: "play",
      title: "Play Together",
      description: "Questions, Truth or Dare & Spicy decks",
      icon: "bi-controller",
      status: "idle",
      statusText: "Questions, Truth or Dare & Spicy decks",
      actionLabel: "Play",
      interactive: true,
      priority: TIER_IDLE,
      onActivate: () => {
        appState.gameDialogOpen = true;
        notifyStateChange();
      },
    };
  }

  // --- Listen Together (Party Room) ---
  let listen: ActivityCardConfig;
  if (partyIncomingInvite) {
    listen = {
      id: "listen",
      title: "Listen Together",
      description: "Synchronized music, together",
      icon: "bi-music-note-beamed",
      status: "invite",
      statusText: `${partyIncomingInvite.fromName} wants to listen together`,
      interactive: false,
      actionLabel: "Waiting",
      priority: TIER_INVITE,
    };
  } else if (partySession) {
    listen = {
      id: "listen",
      title: "Listen Together",
      description: "Synchronized music, together",
      icon: "bi-music-note-beamed",
      status: partySession.status === "pending" ? "pending" : "active",
      statusText: partySession.status === "pending" ? `Waiting for ${partySession.peerName}…` : `Listening with ${partySession.peerName}`,
      interactive: false,
      actionLabel: "In progress",
      priority: TIER_ACTIVE,
    };
  } else {
    listen = {
      id: "listen",
      title: "Listen Together",
      description: "Synchronized music, together",
      icon: "bi-music-note-beamed",
      status: "idle",
      statusText: "Synchronized music, together",
      actionLabel: "Listen",
      interactive: true,
      priority: TIER_IDLE,
      onActivate: () => {
        appState.partyInviteDialogOpen = true;
        notifyStateChange();
      },
    };
  }

  // --- Couple Wheel ---
  // Unlike Play/Listen above, this feature is route-driven (a real
  // /couple-wheel page, not a session-state overlay) - so every non-idle
  // status still gets an onActivate that just navigates there, since
  // that's the only way back into an in-progress/pending session too, not
  // just the way to start a new one.
  let wheel: ActivityCardConfig;
  if (wheelIncomingInvite) {
    wheel = {
      id: "wheel",
      title: "Couple Wheel",
      description: "Spin together, land on the same result",
      icon: "bi-suit-heart-fill",
      status: "invite",
      statusText: `${wheelIncomingInvite.fromName} wants to spin the wheel`,
      interactive: true,
      actionLabel: "Respond",
      priority: TIER_INVITE,
      onActivate: () => navigate("/couple-wheel"),
    };
  } else if (wheelSession) {
    wheel = {
      id: "wheel",
      title: "Couple Wheel",
      description: "Spin together, land on the same result",
      icon: "bi-suit-heart-fill",
      status: wheelSession.status === "pending" ? "pending" : "active",
      statusText:
        wheelSession.status === "pending" ? `Waiting for ${wheelSession.peerName}…` : `Spinning with ${wheelSession.peerName}`,
      actionLabel: "Resume",
      interactive: true,
      priority: TIER_ACTIVE,
      onActivate: () => navigate("/couple-wheel"),
    };
  } else {
    wheel = {
      id: "wheel",
      title: "Couple Wheel",
      description: "Spin together, land on the same result",
      icon: "bi-suit-heart-fill",
      status: "idle",
      statusText: "Spin together, land on the same result",
      actionLabel: "Spin",
      interactive: true,
      priority: TIER_IDLE,
      onActivate: () => navigate("/couple-wheel"),
    };
  }

  // --- Gift ---
  // A Private Drop gets its own, more specific card state (never a real
  // media thumbnail, per the brief - just status text/a countdown) rather
  // than folding into the generic "N new gifts waiting" copy below, since
  // a ready drop is a bigger moment than an ordinary unread gift. A locked
  // (still counting down) drop also gets a card, even though it's not
  // "unread" in the actionable sense unreadGiftCount() counts - the user
  // can't open it yet, but knowing something is coming is the point.
  const readyDrop = gifts.find((g) => g.direction === "received" && g.deliveryMode === "unlock_later" && !g.locked && !g.viewedAt) ?? null;
  const lockedDrop = gifts.find((g) => g.direction === "received" && g.deliveryMode === "unlock_later" && g.locked) ?? null;
  const unread = unreadGiftCount(gifts);

  let gift: ActivityCardConfig;
  if (readyDrop) {
    gift = {
      id: "gift",
      title: "Private Drop",
      description: "Send a private photo or video gift",
      icon: "bi-gift-fill",
      status: "ready",
      statusText: "Something just unlocked.",
      badge: "Ready",
      actionLabel: "Open",
      interactive: true,
      priority: TIER_READY,
      onActivate: () => navigate("/gifts"),
    };
  } else if (lockedDrop) {
    gift = {
      id: "gift",
      title: "Private Drop",
      description: "Send a private photo or video gift",
      icon: "bi-lock-fill",
      status: "active",
      statusText: lockedDrop.unlockAt ? `Unlocks ${formatWatchTogetherCountdown(lockedDrop.unlockAt).replace(/^Starts/, "Unlocks")}` : "Something is waiting for you.",
      actionLabel: "View",
      interactive: true,
      priority: TIER_ACTIVE,
      onActivate: () => navigate(`/gifts/drop/${lockedDrop.id}`),
    };
  } else if (unread > 0) {
    gift = {
      id: "gift",
      title: "Surprise Your Partner",
      description: "Send a private photo or video gift",
      icon: "bi-gift-fill",
      status: "ready",
      statusText: `${unread} new gift${unread === 1 ? "" : "s"} waiting`,
      badge: String(unread),
      actionLabel: "Open",
      interactive: true,
      priority: TIER_READY,
      onActivate: () => navigate("/gifts"),
    };
  } else {
    gift = {
      id: "gift",
      title: "Surprise Your Partner",
      description: "Send a private photo or video gift",
      icon: "bi-gift-fill",
      status: "idle",
      statusText: "Send a private photo or video gift",
      actionLabel: "Send Gift",
      interactive: true,
      priority: TIER_IDLE,
      onActivate: () => navigate("/gifts"),
    };
  }

  // Array.prototype.sort is stable (ES2019+), so ties within the same
  // priority tier keep this input order - idle default is Play, Listen,
  // Gift, Watch (Watch needs a gallery detour to start, so it's least
  // "launchable straight from Home" when nothing else is going on); an
  // invite/ready/active card still jumps ahead of all of these via its
  // higher tier regardless of this base order.
  return [play, listen, wheel, gift, watch].sort((a, b) => b.priority - a.priority);
}
